import { resolveCohortIds } from '@/components/cohorts/cohort-multi-select';
import { useMyCohorts } from '@/hooks/queries/cohorts';
import { useMyLearningPaths } from '@/hooks/queries/learning-paths';
import PromptService from '@/services/prompt-service';
import {
  filterItemsByCohortIds,
  getUniqueLearningPathPrompts,
} from '@/utils/cohort-filter';
import { withPracticeAnalytics } from '@/utils/practice-payload';
import { ScrollShadow, Selection, Skeleton } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCreateOrUpdatePractice } from 'Src/hooks/queries/practices';
import { useAuthStore } from 'Src/store/use-auth-store';
import { useTenantStore } from 'Src/store/use-tenant-store';
import { PromptData, StandardResponse } from 'Src/types/agent';
import { getGradientForIndexAsString } from '../../../utils/colors';
import ActionTile from './action-tile';

// LiFT mode tags in order of priority
const LIFT_MODE_TAGS = ['Counselor', 'Action', 'Coach'] as const;

interface PromptWithTag extends PromptData {
  tag?: string;
  tags?: Array<{ value: string } | string>;
}

/**
 * Get the color tag for a prompt based on its tags.
 * Priority:
 * 1. If agent has a LiFT mode tag (Counselor, Action, Coach), use the first one found
 * 2. If agent only has Promotion tag, default to 'Counselor' (Inner Mastery Guide Blue)
 */
function getColorTagForPrompt(task: PromptWithTag): string {
  if (!task.tags || task.tags.length === 0) {
    // No tags array, fall back to legacy behavior or default
    return task.tag || 'Counselor';
  }

  // Extract tag values (handle both string and object formats)
  const tagValues = task.tags.map((t) => (typeof t === 'string' ? t : t.value));

  // Find the first LiFT mode tag in the order they appear in the prompt's tags
  for (const tagValue of tagValues) {
    if (LIFT_MODE_TAGS.includes(tagValue as (typeof LIFT_MODE_TAGS)[number])) {
      return tagValue;
    }
  }

  // No LiFT mode tag found, default to Counselor (Inner Mastery Guide Blue)
  return 'Counselor';
}

const TasksCarousel = ({
  selectedCohortKeys,
}: {
  selectedCohortKeys: Selection;
}) => {
  const orgId = useAuthStore((state) => state.orgId);
  const tenantConfig = useTenantStore((state) => state.config);
  const { data: cohorts } = useMyCohorts();
  const createPracticeMutation = useCreateOrUpdatePractice();
  const navigate = useNavigate();
  const [loadingPracticeId, setLoadingPracticeId] = useState<number | null>(
    null
  );

  const sectionTitle =
    tenantConfig?.dashboard_config?.promotion_bar_title || 'Suggested for You';

  const {
    data,
    isLoading: isLoadingLearningPaths,
    isError,
  } = useMyLearningPaths({
    tag: 'Promotion',
  });

  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<
    StandardResponse<PromptData>
  >({
    queryKey: [`agent-promotion`, { is_active: true, tag: 'Promotion' }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag: 'Promotion',
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  // Flatten prompts from learning paths while preserving their tag and tags array
  const selectedCohortIds = useMemo(
    () => resolveCohortIds(selectedCohortKeys, cohorts?.results ?? []),
    [selectedCohortKeys, cohorts?.results]
  );

  const allCohortIds = useMemo(
    () => cohorts?.results?.map((cohort) => cohort.id) ?? [],
    [cohorts?.results]
  );

  const scenarios = useMemo((): PromptWithTag[] => {
    if (!data?.results) return [];

    return getUniqueLearningPathPrompts(
      data.results,
      selectedCohortIds,
      allCohortIds,
      (entry, path): PromptWithTag => ({
        ...entry.prompt,
        is_active: entry.prompt.is_active ?? true,
        tag: path.tag,
        tags: (entry.prompt as PromptWithTag).tags,
      })
    );
  }, [data, selectedCohortIds, allCohortIds]);

  const promptsData = useMemo((): PromptWithTag[] => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return scenarios;
    }

    const promptItems = (prompts?.results || []).map((p) => ({
      ...p,
      tags: (p as PromptWithTag).tags,
    }));

    return filterItemsByCohortIds(
      promptItems,
      selectedCohortIds,
      allCohortIds
    );
  }, [prompts, scenarios, selectedCohortIds, allCohortIds]);

  const isLoading = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return isLoadingLearningPaths;
    }
    return isLoadingPrompts;
  }, [isLoadingPrompts, isLoadingLearningPaths]);

  const handleScenarioClick = async (
    task: PromptWithTag,
    categoryTag: string
  ) => {
    try {
      setLoadingPracticeId(task.id);
      const response = (await createPracticeMutation.mutateAsync(
        withPracticeAnalytics(
          {
            name: task.name,
            org_id: orgId,
          },
          selectedCohortIds
        )
      )) as { id: number };

      navigate(
        `${response.id}/run/?action=${encodeURIComponent(task.name)}&id=${response.id}&tag=${encodeURIComponent(categoryTag)}`
      );
    } catch (error) {
      toast.error('Failed to create practice session');
      console.error(error);
    } finally {
      setLoadingPracticeId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col mb-8">
        <h2 className="text-lg font-semibold text-foreground mb-8">
          {sectionTitle}
        </h2>
        <div className="flex gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={index}
              className="w-full md:w-[200px] aspect-square md:h-[200px] shrink-0 min-w-0 rounded-2xl border-0 transition-shadow hover:shadow-lg"
            />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return <div>Error loading tasks</div>;
  }

  if (!promptsData?.length) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2" aria-label="Suggested scenarios">
      <h2 className="text-xl font-semibold text-foreground mb-2">
        {sectionTitle}
      </h2>
      <ScrollShadow
        orientation="horizontal"
        hideScrollBar
        className="w-full h-full relative mb-8"
        size={100}
      >
        <div className="flex gap-4 py-4 w-full h-full">
          {promptsData.map((task, index) => {
            // Get the color tag based on LiFT mode tags, fallback to Counselor
            const colorTag = getColorTagForPrompt(task);
            // Use the learning path tag for navigation, fallback to color tag
            const categoryTag = task.tag || colorTag;
            const bgColor = getGradientForIndexAsString(
              colorTag,
              index,
              true,
              0.75
            );
            const hoverBgColor = getGradientForIndexAsString(
              colorTag,
              index,
              true,
              0.85
            );
            return (
              <ActionTile
                key={task.id}
                title={task.name}
                // description={task.system_prompt}
                bgColor={bgColor}
                hoverBgColor={hoverBgColor}
                onAction={() => handleScenarioClick(task, categoryTag)}
                isLoading={loadingPracticeId === task.id}
              />
            );
          })}
        </div>
      </ScrollShadow>
    </section>
  );
};

export default TasksCarousel;
